/**
 * useAnnouncements Hook
 *
 * Manages system-wide announcements from admins to all users.
 *
 * Architecture:
 * - useAnnouncementsListener: Sets up Firestore listener, called ONCE in AuthenticatedLayout
 * - useAnnouncements: Reads from Zustand store, used by all components
 *
 * This eliminates redundant Firestore subscriptions that occurred when
 * multiple components (PageContainer, UserProfile, IndexPage) each called
 * useAnnouncements and created their own listeners.
 *
 * Features:
 * - Real-time listener for announcements collection (single instance)
 * - Popup logic: Only shows announcements from last 3 days that haven't been dismissed
 * - localStorage tracking for dismissed announcements
 * - History view: All active announcements (regardless of age)
 *
 * New User Protection:
 * - New users only see announcements from the last 3 days as popups
 * - Prevents being bombarded with old announcements
 */

import { useCallback, useMemo } from 'react';
import { useFirebaseStore } from '../store/firebaseStore';
import { useFirestoreCollection } from './firestore';
import { orderBy } from 'firebase/firestore';
import type { DocumentData, Timestamp, QueryConstraint } from 'firebase/firestore';
import {
  useAnnouncementsStore,
  type Announcement,
  type AnnouncementType,
  type DisplayMode,
  type AnnouncementImage,
} from '../store/announcementsStore';

// Re-export types from store for backwards compatibility
export type { Announcement, AnnouncementType, DisplayMode, AnnouncementImage };

/** Collection path for announcements (subcollection under appSettings) */
const ANNOUNCEMENTS_PATH = 'config/appSettings/announcements';

/**
 * Parse announcement document from Firestore
 */
function parseAnnouncement(id: string, data: DocumentData): Announcement {
  const createdAt = data.createdAt as Timestamp;
  const scheduledAt = data.scheduledAt as Timestamp | undefined;
  const announcement: Announcement = {
    id,
    title: data.title || '',
    message: data.message || '',
    createdAt: createdAt?.toDate() || new Date(),
    createdBy: data.createdBy || '',
    active: data.active !== false, // Default to true
    type: data.type || 'urgent', // Default to 'urgent' for backwards compatibility
  };

  // Add rich announcement fields if present
  if (data.displayMode) {
    announcement.displayMode = data.displayMode;
  }
  if (data.htmlContent) {
    announcement.htmlContent = data.htmlContent;
  }
  if (data.contentJSON) {
    announcement.contentJSON = data.contentJSON;
  }
  if (data.images && Array.isArray(data.images)) {
    announcement.images = data.images;
  }
  // Add scheduled time if present
  if (scheduledAt) {
    announcement.scheduledAt = scheduledAt.toDate();
  }

  return announcement;
}

/** Return type for useAnnouncements hook */
export interface UseAnnouncementsReturn {
  /** Announcements to show in popup (urgent only, within 3 days, not dismissed, active) */
  popupAnnouncements: Announcement[];
  /** All active announcements for history view */
  allAnnouncements: Announcement[];
  /** All active announcements for Index page display (both types) */
  indexPageAnnouncements: Announcement[];
  /** Number of unread popup announcements (urgent only) */
  unreadCount: number;
  /** Number of unread announcements for index page */
  unreadIndexCount: number;
  /** Mark all popup announcements as dismissed */
  dismissAll: () => void;
  /** Mark a specific announcement as read */
  markAsRead: (id: string) => void;
  /** Check if a specific announcement has been read */
  isRead: (id: string) => boolean;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
}

/**
 * Hook to set up the Firestore announcements listener.
 *
 * IMPORTANT: Call this ONCE in AuthenticatedLayout.
 * It populates the Zustand store that all components read from.
 *
 * @param enabled - Only activate listener when true (default: true). Pass isConnected
 *                  to prevent listener setup before Firebase auth completes.
 */
export const useAnnouncementsListener = (enabled: boolean = true): void => {
  const db = useFirebaseStore((state) => state.db);
  const { setAnnouncements, setIsLoading, setError } = useAnnouncementsStore();

  // Query constraints - order by createdAt descending
  const queryConstraints: QueryConstraint[] = useMemo(
    () => [orderBy('createdAt', 'desc')],
    []
  );

  // Handle announcements loaded
  const handleAnnouncementsLoaded = useCallback(
    (items: Announcement[]) => {
      setAnnouncements(items);
    },
    [setAnnouncements]
  );

  // Handle loading change
  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      setIsLoading(loading);
    },
    [setIsLoading]
  );

  // Handle error
  const handleError = useCallback(
    (errorMessage: string | null) => {
      setError(errorMessage);
    },
    [setError]
  );

  // Set up Firestore listener - this is the ONLY listener for announcements
  // Only enable when `enabled` prop is true (prevents listener before auth)
  useFirestoreCollection<Announcement>({
    db,
    collectionPath: ANNOUNCEMENTS_PATH,
    queryConstraints,
    parseDocument: parseAnnouncement,
    onDataLoaded: handleAnnouncementsLoaded,
    onLoadingChange: handleLoadingChange,
    onError: handleError,
    enabled: enabled && !!db,
  });
};

/**
 * Hook for consuming announcements data.
 *
 * This reads from the Zustand store populated by useAnnouncementsListener.
 * Safe to call from any component - does NOT create a Firestore listener.
 */
export const useAnnouncements = (): UseAnnouncementsReturn => {
  const store = useAnnouncementsStore();

  // Get computed values
  const allAnnouncements = store.getAllAnnouncements();
  const popupAnnouncements = store.getPopupAnnouncements();
  const indexPageAnnouncements = store.getIndexPageAnnouncements();
  const unreadCount = store.getUnreadCount();
  const unreadIndexCount = store.getUnreadIndexCount();

  // Wrap isRead in a stable callback for consumers
  const isRead = useCallback((id: string) => store.isRead(id), [store]);

  return {
    popupAnnouncements,
    allAnnouncements,
    indexPageAnnouncements,
    unreadCount,
    unreadIndexCount,
    dismissAll: store.dismissAll,
    markAsRead: store.markAsRead,
    isRead,
    isLoading: store.isLoading,
    error: store.error,
  };
};

export default useAnnouncements;
